import React, { useState, useEffect, useMemo } from 'react';
import { 
  Users, 
  // UserPlus, // temporarily removed
  Edit, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  UserCheck,
  UserX,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown
} from 'lucide-react';
import PageHeader from '../components/PageHeader';
import { useEffectivePermissions } from '../hooks/useEffectivePermissions';
import { rbacService } from '../../backend/services/rbac';
import { formatDate } from '../../shared/utils';
import type { UserWithRoles, Role } from '../../shared/types/auth';
// import { useActivityLogger } from '../hooks/useActivityLogger'; // temporarily removed
// import { FEATURE_NAMES } from '../../shared/types/user-management'; // temporarily removed
import { toast } from 'sonner';
import UserProfileModal from '../components/modals/UserProfileModal';

export const AdminUsers: React.FC = () => {
  const { hasRole: effectiveHasRole } = useEffectivePermissions();
  const [users, setUsers] = useState<UserWithRoles[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<string>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  // const [selectedUsers, setSelectedUsers] = useState<string[]>([]); // Removed - no bulk actions needed
  const [showUserModal, setShowUserModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserWithRoles | null>(null);
  // const [modalMode, setModalMode] = useState<'edit' | 'invite'>('edit'); // temporarily removed
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Check if user can manage users
  const canManageUsers = effectiveHasRole('admin') || effectiveHasRole('super');

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const usersData = await rbacService.getAllUsers();
      
      // NEW: Debug logs for fetched users
      console.debug('AdminUsers: Fetched users from RBAC service:', usersData.length);
      console.debug('AdminUsers: User data:', JSON.stringify(usersData, null, 2));
      
      setUsers(usersData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch users');
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const rolesData = await rbacService.getRoles();
      console.debug('AdminUsers: Fetched roles:', rolesData.length, rolesData);
      setRoles(rolesData);
    } catch (err) {
      console.error('Failed to fetch roles:', err);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, []);

  // NEW: Debug log when users state changes
  useEffect(() => {
    console.debug('AdminUsers: Users state updated:', users.length);
    console.debug('AdminUsers: First user example:', users[0] ? JSON.stringify(users[0], null, 2) : 'No users');
  }, [users]);

  // Sort users
  const filteredUsers = useMemo(() => {
    let filtered = [...users];

    // Sort users
    filtered.sort((a, b) => {
      let aValue: any, bValue: any;
      
      switch (sortBy) {
        case 'name':
          // Handle unnamed users - put them at the bottom
          const aHasName = a.first_name && a.last_name;
          const bHasName = b.first_name && b.last_name;
          
          if (aHasName && !bHasName) {
            aValue = 0; // a comes first
            bValue = 1; // b comes after
          } else if (!aHasName && bHasName) {
            aValue = 1; // a comes after
            bValue = 0; // b comes first
          } else if (!aHasName && !bHasName) {
            // Both unnamed - sort by email
            aValue = a.email.toLowerCase();
            bValue = b.email.toLowerCase();
          } else {
            // Both have names - sort by full name
            aValue = `${a.first_name} ${a.last_name}`.toLowerCase();
            bValue = `${b.first_name} ${b.last_name}`.toLowerCase();
          }
          break;
        case 'email':
          aValue = a.email.toLowerCase();
          bValue = b.email.toLowerCase();
          break;
        case 'status':
          aValue = (a.status || (a.email_confirmed_at ? 'active' : 'pending')).toLowerCase();
          bValue = (b.status || (b.email_confirmed_at ? 'active' : 'pending')).toLowerCase();
          break;
        case 'last_sign_in_at':
          aValue = new Date(a.last_sign_in_at || 0).getTime();
          bValue = new Date(b.last_sign_in_at || 0).getTime();
          break;
        case 'created_at':
        default:
          aValue = new Date(a.created_at || 0).getTime();
          bValue = new Date(b.created_at || 0).getTime();
          break;
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  }, [users, sortBy, sortOrder]);

  const handleUserAction = async (userId: string, action: string) => {
    if (!canManageUsers) {
      toast.error('You do not have permission to perform this action');
      return;
    }

    // Check if user is super admin
    const user = users.find(u => u.id === userId);
    const isSuperAdmin = user?.roles.some(role => role && role.name === 'super');

    // Prevent deletion of super admin
    if (action === 'delete' && isSuperAdmin) {
      toast.error('Cannot delete super admin user');
      return;
    }

    // Prevent suspension of super admin
    if ((action === 'suspend' || action === 'activate') && isSuperAdmin) {
      toast.error('Cannot suspend or activate super admin user');
      return;
    }

    setActionLoading(userId);
    try {
      switch (action) {
        case 'edit':
          if (user) {
            setSelectedUser(user);
            setShowUserModal(true);
          }
          break;
        case 'suspend':
          await rbacService.suspendUser(userId, true);
          toast.success('User suspended');
          await fetchUsers();
          break;
        case 'activate':
          await rbacService.suspendUser(userId, false);
          toast.success('User activated');
          await fetchUsers();
          break;
        case 'reset-password':
          await rbacService.resetUserPassword(userId);
          toast.success('Password reset email sent');
          break;
        case 'delete':
          if (confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
            await rbacService.deleteUser(userId);
            toast.success('User deleted');
            await fetchUsers();
          }
          break;
      }
    } catch (err) {
      toast.error(`Failed to ${action.replace('-', ' ')} user`);
    } finally {
      setActionLoading(null);
    }
  };

  // Bulk actions removed - not needed for small user base

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const getSortIcon = (field: string) => {
    if (sortBy !== field) {
      return <ChevronsUpDown className="h-4 w-4 text-gray-400" />;
    }
    return sortOrder === 'asc' 
      ? <ChevronUp className="h-4 w-4 text-blue-600" />
      : <ChevronDown className="h-4 w-4 text-blue-600" />;
  };

  const getRoleColor = (roleName: string) => {
    switch (roleName) {
      case 'super': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'admin': return 'bg-red-100 text-red-800 border-red-200';
      case 'manager': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'user': return 'bg-green-100 text-green-800 border-green-200';
      case 'client': return 'bg-gray-100 text-gray-800 border-gray-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (user: UserWithRoles) => {
    const status = user.status || (user.email_confirmed_at ? 'active' : 'pending');
    
    switch (status) {
      case 'active':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'pending':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'inactive':
        return <AlertCircle className="h-4 w-4 text-yellow-500" />;
      case 'suspended':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <AlertCircle className="h-4 w-4 text-gray-500" />;
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-24 bg-gray-100 rounded"></div>
              ))}
            </div>
            <div className="h-96 bg-gray-100 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  console.debug('AdminUsers: Rendering with', users.length, 'users');

  return (
    <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-full">
      <div className="max-w-7xl mx-auto">
        <PageHeader
          icon={<Users className="h-8 w-8" />}
          title="User Management"
          actions={canManageUsers ? (
            <div className="flex gap-2">
              {/* Temporarily hidden until Supabase configuration is fixed */}
              {/* <button
                onClick={() => {
                  setSelectedUser(null);
                  setModalMode('invite');
                  setShowUserModal(true);
                }}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <UserPlus className="h-4 w-4 mr-2" /> Invite User
              </button> */}
              {/* Refresh button removed */}
            </div>
          ) : null}
        />

        {/* Compact Stats */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex space-x-6 text-sm text-gray-600">
            <span><strong className="text-gray-900">{users.length}</strong> total</span>
            <span><strong className="text-green-600">{users.filter(u => (u.status || (u.email_confirmed_at ? 'active' : 'pending')) === 'active').length}</strong> active</span>
            <span><strong className="text-purple-600">{users.filter(u => u.roles.some(r => r && (r.name === 'admin' || r.name === 'super'))).length}</strong> admins</span>
          </div>
        </div>

        {/* Filters removed - using table header sorting instead */}

        {/* Bulk actions removed - not needed for small user base */}

        {/* Users Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {/* Checkbox column removed - no bulk actions needed */}
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>User</span>
                      {getSortIcon('name')}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Roles
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('status')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Status</span>
                      {getSortIcon('status')}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('last_sign_in_at')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Last Active</span>
                      {getSortIcon('last_sign_in_at')}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('created_at')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Created</span>
                      {getSortIcon('created_at')}
                    </div>
                  </th>
                  {canManageUsers && (
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    {/* Checkbox removed - no bulk actions needed */}
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-3">
                        <div className="flex-shrink-0 h-8 w-8">
                          <div className="h-8 w-8 rounded-full bg-gray-300 flex items-center justify-center">
                            <span className="text-xs font-medium text-gray-700">
                              {user.first_name?.[0] || user.email[0].toUpperCase()}
                            </span>
                          </div>
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-gray-900 truncate">
                            {user.first_name && user.last_name 
                              ? `${user.first_name} ${user.last_name}`
                              : 'Unnamed User'
                            }
                          </div>
                          <div className="text-xs text-gray-500 truncate">{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-wrap gap-1">
                        {user.roles.filter(role => role).map((role) => (
                          <span
                            key={role.id}
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getRoleColor(role?.name || 'unknown')}`}
                          >
                            {role?.name || 'Unknown'}
                          </span>
                        ))}
                        {user.roles.length === 0 && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border bg-gray-100 text-gray-800 border-gray-200">
                            No roles
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {getStatusIcon(user)}
                        <span className="ml-2 text-sm text-gray-900">
                          {(() => {
                            const status = user.status || (user.email_confirmed_at ? 'active' : 'pending');
                            switch (status) {
                              case 'active': return 'Active';
                              case 'pending': return 'Pending';
                              case 'inactive': return 'Inactive';
                              case 'suspended': return 'Suspended';
                              default: return 'Unknown';
                            }
                          })()}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.last_sign_in_at ? formatDate(user.last_sign_in_at) : 'Never'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(user.created_at || '')}
                    </td>
                    {canManageUsers && (
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => handleUserAction(user.id, 'edit')}
                            className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors"
                            title="Edit user profile and roles"
                          >
                            <Edit className="h-3 w-3 mr-1" />
                            Edit
                          </button>
                          <button
                            onClick={() => {
                              const status = user.status || (user.email_confirmed_at ? 'active' : 'pending');
                              handleUserAction(user.id, status === 'active' ? 'suspend' : 'activate');
                            }}
                            disabled={actionLoading === user.id || user.roles.some(role => role && role.name === 'super')}
                            className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-md transition-colors disabled:opacity-50 ${
                              (() => {
                                const status = user.status || (user.email_confirmed_at ? 'active' : 'pending');
                                return status === 'active' 
                                  ? 'text-yellow-600 bg-yellow-50 hover:bg-yellow-100' 
                                  : 'text-green-600 bg-green-50 hover:bg-green-100';
                              })()
                            }`}
                            title={user.roles.some(role => role && role.name === 'super') ? 'Cannot modify super admin' : (() => {
                              const status = user.status || (user.email_confirmed_at ? 'active' : 'pending');
                              return status === 'active' ? 'Suspend user account' : 'Activate user account';
                            })()}
                          >
                            {(() => {
                              const status = user.status || (user.email_confirmed_at ? 'active' : 'pending');
                              return status === 'active' ? (
                                <>
                                  <UserX className="h-3 w-3 mr-1" />
                                  Suspend
                                </>
                              ) : (
                                <>
                                  <UserCheck className="h-3 w-3 mr-1" />
                                  Activate
                                </>
                              );
                            })()}
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {filteredUsers.length === 0 && (
          <div className="text-center py-12">
            <Users className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No users found</h3>
            <p className="mt-1 text-sm text-gray-500">
              No users are currently in the system.
            </p>
          </div>
        )}

        {error && (
          <div className="mt-6 bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <XCircle className="h-5 w-5 text-red-400" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <div className="mt-2 text-sm text-red-700">{error}</div>
              </div>
            </div>
          </div>
        )}

        {/* Modal */}
        <UserProfileModal
          user={selectedUser}
          isOpen={showUserModal}
          onClose={() => {
            setShowUserModal(false);
            setSelectedUser(null);
          }}
          onUserUpdated={fetchUsers}
          roles={roles}
          mode="edit"
        />
      </div>
    </div>
  );
};

export default AdminUsers;
